import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { Accordion, CardNotification, Text } from "@foris/avocado-suite";
import StageProgressCard from "../stage-progress-card";
import { ProgrammingSummary as ProgrammingSummaryType } from "@services/types/programming-edit/programmingEditTypes";
import { useProgrammingEditStore } from "@context/useProgrammingEditStore";
import { hasStageDependencyViolation } from "../../utils/stageDependency";
import styles from "./programmingSummary.module.scss";

export interface ProgrammingValidationState {
  /** Whether any stage has an incomplete block assignment (partial or zero). */
  hasCompletenessWarnings: boolean;
  /** Whether any stage has 0 blocks assigned. */
  hasUnscheduledWarnings: boolean;
  /** Whether any stage violates its dependency constraint (date order or missing prerequisite assignment). */
  hasDependencyWarnings: boolean;
}

interface ProgrammingSummaryProps {
  summary: ProgrammingSummaryType;
  /**
   * Whether the user can skip the stage completeness validation.
   * When true, dependency stages only need at least one block assigned (not 100%).
   */
  canSkipCompleteness?: boolean;
  /**
   * Whether the user can skip the unscheduled-stage validation.
   * When true, stages may remain with 0 blocks assigned.
   */
  canSkipUnscheduled?: boolean;
  /** Called whenever the validation state changes, reporting categorized warning types. */
  onValidationState?: (state: ProgrammingValidationState) => void;
}

const ProgrammingSummary: React.FC<ProgrammingSummaryProps> = ({
  summary,
  canSkipCompleteness,
  canSkipUnscheduled,
  onValidationState,
}) => {
  const { t } = useTranslation();
  const hasChanges = useProgrammingEditStore(state => state.hasChanges);
  const slots = useProgrammingEditStore(state => state.slots);
  const stages = useProgrammingEditStore(state => state.stages);

  /**
   * Computes warnings categorized by type:
   * - completeness: a stage is partially or over assigned
   * - unscheduled: a stage has 0 blocks assigned
   * - dependency: a stage violates date order, its prerequisite has no assignment,
   *   or (when canSkipCompleteness is false) the prerequisite is not complete
   */
  const {
    warnings,
    hasCompletenessWarnings,
    hasUnscheduledWarnings,
    hasDependencyWarnings,
  } = useMemo(() => {
    const items: {
      stageName: string;
      detail: string;
      type: "completeness" | "unscheduled" | "dependency";
    }[] = [];
    const stageByCode = new Map(stages.map(s => [s.code, s]));
    const stageProgressById = new Map(summary.stages.map(s => [s.id, s]));

    summary.stages.forEach(stage => {
      const missingBlocks = stage.totalBlocks - stage.assignedBlocks;

      if (!canSkipUnscheduled && stage.assignedBlocks === 0) {
        items.push({
          type: "unscheduled",
          stageName: t("programming-edit.summary.warnings.stage-unscheduled", {
            stageName: stage.name,
          }),
          detail: t("programming-edit.summary.warnings.stage-unscheduled-detail"),
        });
      }

      if (stage.assignedBlocks > 0 && missingBlocks > 0) {
        const missingHours = missingBlocks * 0.75;
        items.push({
          type: "completeness",
          stageName: t("programming-edit.summary.warnings.stage-incomplete", {
            stageName: stage.name,
          }),
          detail: t("programming-edit.summary.warnings.missing-blocks", {
            missingBlocks,
            missingHours,
          }),
        });
      }

      if (stage.assignedBlocks > stage.totalBlocks) {
        items.push({
          type: "completeness",
          stageName: t("programming-edit.summary.warnings.stage-over-assigned", {
            stageName: stage.name,
          }),
          detail: "",
        });
      }

      const adaptedStage = stages.find(s => s.id === stage.id);
      if (adaptedStage?.dependencyCodes?.length > 0) {
        const currentSlots = slots.filter(s => s.stageId === stage.id);
        if (currentSlots.length === 0) return;

        adaptedStage.dependencyCodes.forEach(depCode => {
          const depStage = stageByCode.get(depCode);
          if (!depStage) return;

          const depSlots = slots.filter(s => s.stageId === depStage.id);
          const depProgress = stageProgressById.get(depStage.id);

          /**
           * A dependency stage must have at least one block assigned.
           * When canSkipCompleteness is false, it must also be fully complete (100%).
           */
          const depHasNoAssignment = (depProgress?.assignedBlocks ?? 0) === 0;
          const depIsIncomplete = !canSkipCompleteness && !depProgress?.isComplete;

          if (depHasNoAssignment || depIsIncomplete) {
            const depName = depStage.name ?? depCode;
            items.push({
              type: "dependency",
              stageName: t("programming-edit.summary.warnings.dependency-violation-title", {
                stageName: stage.name,
                depName,
              }),
              detail: t("programming-edit.summary.warnings.dependency-violation-detail", {
                depName,
              }),
            });
            return;
          }

          /**
           * Validates that slots assigned to this stage are scheduled after
           * all slots of its dependency stage. A stage with `dependencyCodes`
           * must have its earliest date strictly after the latest date of
           * each dependency stage.
           */
          if (hasStageDependencyViolation(currentSlots, depSlots)) {
            const depName = depStage.name ?? depCode;
            items.push({
              type: "dependency",
              stageName: t("programming-edit.summary.warnings.dependency-violation-title", {
                stageName: stage.name,
                depName,
              }),
              detail: t("programming-edit.summary.warnings.dependency-violation-detail", {
                depName,
              }),
            });
          }
        });
      }
    });

    return {
      warnings: items,
      hasCompletenessWarnings: items.some(w => w.type === "completeness"),
      hasUnscheduledWarnings: items.some(w => w.type === "unscheduled"),
      hasDependencyWarnings: items.some(w => w.type === "dependency"),
    };
  }, [summary.stages, slots, stages, canSkipCompleteness, canSkipUnscheduled]);

  useEffect(() => {
    onValidationState?.({
      hasCompletenessWarnings,
      hasUnscheduledWarnings,
      hasDependencyWarnings,
    });

    return () => {
      onValidationState?.({
        hasCompletenessWarnings: false,
        hasUnscheduledWarnings: false,
        hasDependencyWarnings: false,
      });
    };
  }, [hasCompletenessWarnings, hasUnscheduledWarnings, hasDependencyWarnings]);

  return (
    <>
      <Accordion
        className={styles.programmingSummary}
        title={t("programming-edit.summary.title")}
        customContent={
          <div className={styles.programmingSummary_stats}>
            <Text type="sm" className={cx(styles.programmingSummary_statItem)}>
              <span className={styles.programmingSummary_statValue}>
                {summary.completedStages}/{summary.totalStages}
              </span>{" "}
              {t("programming-edit.summary.completed-stages")}
            </Text>
            <span className={styles.programmingSummary_dotSeparator}>•</span>
            <Text
              type="sm"
              className={cx(
                styles.programmingSummary_statItem,
                styles.programmingSummary_statItem__info,
              )}
            >
              <span className={styles.programmingSummary_statValue}>{summary.classesToAssign}</span>{" "}
              {t("programming-edit.summary.classes-to-assign")}
            </Text>
            <span className={styles.programmingSummary_dotSeparator}>•</span>
            <Text
              type="sm"
              className={cx(
                styles.programmingSummary_statItem,
                styles.programmingSummary_statItem__info,
              )}
            >
              <span className={styles.programmingSummary_statValue}>{summary.pendingBlocks}</span>{" "}
              {t("programming-edit.summary.pending-blocks")}
            </Text>
            {summary.incompleteCount > 0 && (
              <>
                <span className={styles.programmingSummary_dotSeparator}>•</span>
                <Text
                  type="sm"
                  className={cx(
                    styles.programmingSummary_statItem,
                    styles.programmingSummary_statItem__warning,
                  )}
                >
                  <span className={styles.programmingSummary_statValue}>
                    {summary.incompleteCount}
                  </span>{" "}
                  {t("programming-edit.summary.incomplete")}
                </Text>
              </>
            )}
          </div>
        }
        opened
      >
        <div className={styles.programmingSummary_stages}>
          {summary.stages.map(stage => (
            <StageProgressCard key={stage.id} stage={stage} />
          ))}
        </div>
      </Accordion>

      {hasChanges && warnings.length > 0 && (
        <CardNotification
          className={styles.programmingSummary_warningCard}
          state="warning"
          title={t("programming-edit.summary.review-before-apply")}
          outlined
        >
          <ul className={styles.programmingSummary_warningList}>
            {warnings.map((warning, index) => (
              <li key={index} className={styles.programmingSummary_warningItem}>
                <span className={styles.programmingSummary_warningBold}>{warning.stageName}</span>
                {warning.detail}
              </li>
            ))}
          </ul>
        </CardNotification>
      )}

      {hasChanges && warnings.length === 0 && summary.completedStages === summary.totalStages && (
        <CardNotification
          state="success"
          title={t("programming-edit.summary.ready-to-apply-title")}
          outlined
        >
          {t("programming-edit.summary.ready-to-apply-description")}
        </CardNotification>
      )}
    </>
  );
};

export default ProgrammingSummary;
